/**
 * One canonical instruction bundle for every Brunel AI role (#615 / #618).
 *
 * Instruction composition is separate from execution routing: nothing here
 * chooses a provider, runtime or model, and nothing here is a permission. The
 * allow/deny engine in policy/precedence.ts stays the only authority boundary;
 * a bundle only tells a model what its job is and where the current rules live.
 *
 * Layers are in authority order. A lower layer never overrides a higher one,
 * and the rendered prompt says so. Learned heuristics are the lowest layer and
 * cannot carry authority, spend, production, destructive, credential or
 * security instructions at all — those need the owner.
 */

export const INSTRUCTION_LAYERS = [
  'owner_contract',
  'task',
  'role_playbook',
  'project_guidance',
  'structural_context',
  'learned_heuristic',
] as const;

export type InstructionLayer = (typeof INSTRUCTION_LAYERS)[number];

const LAYER_TITLE: Readonly<Record<InstructionLayer, string>> = {
  owner_contract: 'Owner contract (highest authority)',
  task: 'Current task and owner corrections',
  role_playbook: 'Your role playbook',
  project_guidance: 'Current repository guidance',
  structural_context: 'Durable task-chain context',
  learned_heuristic: 'Learned heuristics (lowest priority; advisory only)',
};

/** Where an instruction came from, so a consumed bundle is auditable without its text. */
export interface InstructionProvenance {
  readonly source: string;
  readonly ref: string;
  readonly version: string | null;
}

export interface InstructionEntry {
  readonly layer: InstructionLayer;
  readonly key: string;
  readonly lines: readonly string[];
  readonly provenance: InstructionProvenance;
}

/** A role's standing identity and job. Data, not prompt code (#618 seeds and supersedes these). */
export interface RolePlaybook {
  readonly role: string;
  readonly identity: string;
  readonly standingJob: string;
  readonly watches: readonly string[];
  readonly produces: readonly string[];
  readonly mustNot: readonly string[];
  readonly reportsTo: string;
  readonly reorientFrom: readonly string[];
  readonly version: string;
  readonly provenance: InstructionProvenance;
}

export type InstructionExclusionReason =
  | 'invalid'
  | 'over_budget'
  | 'secret_shaped'
  | 'requires_owner_authority';

export interface InstructionBundle {
  readonly role: string;
  /** null = no active playbook for this role; rendered as a visible Unknown, never a generic AI. */
  readonly playbook: RolePlaybook | null;
  readonly entries: readonly InstructionEntry[];
  readonly excluded: readonly { readonly key: string; readonly layer: InstructionLayer; readonly reason: InstructionExclusionReason }[];
  /** Stable identity of exactly what was consumed: playbook version + entry keys, versions and text. */
  readonly version: string;
}

const CURRENT_LAYERS: ReadonlySet<InstructionLayer> = new Set(['owner_contract', 'task']);

export const INSTRUCTION_LINE_LIMIT = 500;
export const INSTRUCTION_ENTRY_LINE_LIMIT = 24;
export const INSTRUCTION_BUNDLE_CHAR_LIMIT = 12_000;

/**
 * Learned heuristics touching these domains need owner authority, so they are
 * never composed into a bundle, however they were promoted.
 */
const OWNER_AUTHORITY_DOMAIN =
  /\b(merge|deploy|publish|release|spend|pay|payment|billing|budget|metered|api[- ]?key|credential|password|token|secret|production|prod|migrat\w*|delete|drop|destroy|force[- ]?push|reset --hard|permission|authori[sz]\w*|approv\w*|security|owner rule|provider|runtime|model|route|fallback)\b/i;

const DEFAULT_SECRET_SHAPE =
  /(sk-[A-Za-z0-9_-]{16,}|gh[pousr]_[A-Za-z0-9]{20,}|xox[abprs]-[A-Za-z0-9-]{10,}|AKIA[0-9A-Z]{16}|-----BEGIN [A-Z ]*PRIVATE KEY-----|eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.)/;

/**
 * Whether instruction text touches a domain that needs owner authority. The durable
 * lesson store (#618) classifies proposals with the same domain, so such a lesson can
 * never be promoted, and this resolver drops it even if one were.
 */
export function requiresOwnerAuthority(text: string): boolean {
  return OWNER_AUTHORITY_DOMAIN.test(text);
}

/** Whether text looks like a credential; such text is never stored or composed as an instruction. */
export function isSecretShapedInstruction(text: string): boolean {
  return DEFAULT_SECRET_SHAPE.test(text);
}

function boundedLines(lines: readonly string[]): boolean {
  return lines.length > 0
    && lines.length <= INSTRUCTION_ENTRY_LINE_LIMIT
    && lines.every((line) => typeof line === 'string' && line.trim() !== '' && line.length <= INSTRUCTION_LINE_LIMIT);
}

/** FNV-1a, twice seeded: a deterministic identity for a bundle, not a security hash. */
function fingerprint(text: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ 0x5bd1e995;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    a = Math.imul(a ^ code, 0x01000193) >>> 0;
    b = Math.imul(b ^ code, 0x01000193 + 2) >>> 0;
  }
  return `${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}

/**
 * The owner's mandatory builder preflight. It is a pointer to the current repository rules,
 * not a copied substitute for them, and reaches every AI role through this resolver.
 */
export const OWNER_BUILD_PREFLIGHT: InstructionEntry = Object.freeze({
  layer: 'owner_contract',
  key: 'owner:build-preflight',
  lines: Object.freeze([
    'Build preflight (owner rule): before any build, edit, implementation, migration, deployment, repository-changing command, or other builder work, personally read the current target repository root AGENTS.md first and then its current docs/BUILD-RULES.md when present. For Brunel / CommandOS, both files are mandatory before starting builder work.',
    'This remains mandatory even when the task came only from the Overseer, a Supervisor, automated dispatch, a short starter, or an AI-to-AI handoff. Do not rely on another AI saying it read the rules, and do not rely on copied or remembered rule text.',
    'If required canonical build guidance is unavailable, stop before changing the repository and report the missing guidance instead of guessing.',
  ]),
  provenance: Object.freeze({ source: 'brunel', ref: 'owner_contract:build_preflight', version: null }),
}) as InstructionEntry;

/**
 * The owner's standing role-focus rule (root AGENTS.md §9, "Role focus without capability
 * restriction", #689/#690). It is part of the owner contract, so every bundle any role
 * resolves carries it - Worker, Overseer, Reviewer - wherever that role runs
 * and whichever repository it is in. It focuses a role; it grants and removes nothing.
 */
export const OWNER_ROLE_FOCUS: InstructionEntry = Object.freeze({
  layer: 'owner_contract',
  key: 'owner:role-focus',
  lines: Object.freeze([
    'Role focus (owner rule): your assigned role is your primary professional responsibility. Stay focused on your own domains, responsibilities, evidence and outputs - broad awareness, deep specialization, clear ownership.',
    'You may inspect, research or reason about adjacent domains when that context is needed to do your own role correctly, but do not routinely perform work that belongs to another available specialist.',
    'When work crosses specialties, handle your own portion, name the materially separate specialist portion, and consult or defer it to that role when available. Challenge another specialist when evidence conflicts; specialization never means ignoring relevant evidence or suppressing independent reasoning.',
    'Role focus does not reduce your capabilities, tools, privileges, research access, judgment or any authority granted elsewhere, and grants none. Function, personality, privileges and routing stay separate.',
    'When ownership is ambiguous, the Overseer assigns one primary specialist and may ask other relevant roles for bounded contributions rather than every AI doing the whole job.',
  ]),
  provenance: Object.freeze({ source: 'brunel', ref: 'owner_contract:role_focus', version: null }),
}) as InstructionEntry;

/**
 * The owner's Development Mode contract (root AGENTS.md §2A, #706), condensed. Like role
 * focus it is part of the owner contract, so it reaches every role through this resolver
 * whether or not that role can read Brunel's AGENTS.md - a node host, the hosted Edge, or a
 * Worker in another repository. It grants nothing: production and every existing gate stay
 * where they are.
 */
export const OWNER_DEVELOPMENT_MODE: InstructionEntry = Object.freeze({
  layer: 'owner_contract',
  key: 'owner:development-mode',
  lines: Object.freeze([
    'Development Mode (owner rule): Brunel is in a development/build posture, not production acceptance. Record non-critical defects visibly and keep working when they do not invalidate the current task or a safety boundary. Never present development evidence as production acceptance.',
    'Stop and escalate immediately, without continuing, on a wrong project or repository, secret exposure, destructive or irreversible behavior, a tenant or access-control breach, data-loss or corruption risk, an ambiguous identity or target that could alter the wrong resource, an unauthorized production mutation, or silent runtime/model/provider substitution.',
    'Production stays owner-gated: production deploys, hosted migrations, credential or production-configuration changes, destructive actions and production runtime/model/provider routing changes need the owner\'s explicit authorization. Development Mode grants no authority.',
    'Work only in the exact project, repository and workspace you were given. If the target is ambiguous or missing, fail closed and ask; never fall back to a catch-all project.',
    'Truthful Actual: report what actually ran. Never infer a provider or model result, fake completion, or hide fallback spending.',
    'Owner input always wins: direct owner keyboard or action preempts automation; stop or release when the owner takes over.',
    'No review loops: concrete evidence, a bounded repair and focused green proof settle a blocker; do not add category-based review or re-review.',
  ]),
  provenance: Object.freeze({ source: 'brunel', ref: 'owner_contract:development_mode', version: null }),
}) as InstructionEntry;

/** The one Development Mode line that is this role's own; roles not listed get the common rules only. */
const DEVELOPMENT_MODE_ROLE_LINE: Readonly<Record<string, string>> = Object.freeze({
  worker: 'As a Worker: implement, focused-test, commit, push, and open or update the pull request for your exact task, then leave the durable handoff. The Overseer\'s completion loop reviews and merges.',
  overseer: 'As the Overseer: keep idle Workers productive with approved or pending work through the normal planning and dispatch chain. Never manufacture filler.',
  reviewer: 'As the Reviewer you stay advisory: review a finished run once, pass it or file one finding on that run with its evidence and the smallest correction, and never watch work in progress, start work or review a run again.',
});

/** The Development Mode contract as `role` receives it: the common rules plus its own line. */
export function ownerDevelopmentMode(role: string): InstructionEntry {
  const own = Object.prototype.hasOwnProperty.call(DEVELOPMENT_MODE_ROLE_LINE, role) ? DEVELOPMENT_MODE_ROLE_LINE[role] : undefined;
  if (own === undefined) return OWNER_DEVELOPMENT_MODE;
  return Object.freeze({
    ...OWNER_DEVELOPMENT_MODE,
    lines: Object.freeze([...OWNER_DEVELOPMENT_MODE.lines, own]),
  }) as InstructionEntry;
}

const OWNER_CONTRACT_HASH = /^[0-9a-f]{64}$/;

/**
 * Whether an entry is the one current owner contract (#742). Kept inline so the
 * Edge and Overseer copies of this file stay self-contained. A lesson or a
 * playbook key is not this entry.
 */
function canonicalOwnerContractEntry(entry: InstructionEntry): boolean {
  if (entry.layer !== 'owner_contract' || entry.key !== 'owner:contract') return false;
  const version = entry.provenance.version;
  const hashOk = typeof version === 'string' && OWNER_CONTRACT_HASH.test(version);
  const { source, ref } = entry.provenance;
  if (source === 'repository' && ref === 'AGENTS.md' && hashOk) return true;
  if (source === 'brunel.owner_contract_resources' && ref === 'AGENTS.md' && hashOk) return true;
  if (source === 'brunel.owner_contract_resources' && ref === 'unknown' && version === null) return true;
  return false;
}

/** Visible Unknown used when a canonical resolution produced no contract entry. */
const UNKNOWN_OWNER_CONTRACT: InstructionEntry = Object.freeze({
  layer: 'owner_contract',
  key: 'owner:contract',
  lines: Object.freeze([
    'Owner contract — Unknown. The current canonical owner contract could not be resolved (malformed).',
    'Do not follow a remembered or copied owner rule. Stop before repository changes and report that the owner contract is Unknown.',
  ]),
  provenance: Object.freeze({ source: 'brunel.owner_contract_resources', ref: 'unknown', version: null }),
}) as InstructionEntry;

export function resolveInstructionBundle(input: {
  readonly role: string;
  readonly playbooks: readonly RolePlaybook[];
  readonly entries: readonly InstructionEntry[];
  readonly isSecretShaped?: (text: string) => boolean;
  /**
   * The caller resolved the current root AGENTS.md at execution time. The hard-coded
   * excerpts are not substituted, and only the canonical `owner:contract` entry is kept.
   */
  readonly canonicalOwnerContract?: boolean;
}): InstructionBundle {
  const secretShaped = input.isSecretShaped ?? ((text: string) => DEFAULT_SECRET_SHAPE.test(text));
  const playbook = input.playbooks.find((candidate) => candidate.role === input.role) ?? null;
  const rank = (layer: InstructionLayer) => INSTRUCTION_LAYERS.indexOf(layer);
  const canonical = input.canonicalOwnerContract === true;
  // The owner's build-preflight, role-focus (#690), and Development Mode (#706) rules reach
  // every role through this one resolver, each exactly once; a caller that already carries one keeps its own.
  // A canonical execution-time resolution replaces those excerpts with the current contract.
  const carried = (key: string) => input.entries.some((entry) => entry.key === key);
  const withOwnerRules = canonical ? [...input.entries] : [
    ...input.entries,
    ...(carried(OWNER_BUILD_PREFLIGHT.key) ? [] : [OWNER_BUILD_PREFLIGHT]),
    ...(carried(OWNER_ROLE_FOCUS.key) ? [] : [OWNER_ROLE_FOCUS]),
    ...(carried(OWNER_DEVELOPMENT_MODE.key) ? [] : [ownerDevelopmentMode(input.role)]),
  ];
  const ordered = withOwnerRules
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => rank(a.entry.layer) - rank(b.entry.layer) || a.index - b.index)
    .map(({ entry }) => entry);

  const entries: InstructionEntry[] = [];
  const excluded: { key: string; layer: InstructionLayer; reason: InstructionExclusionReason }[] = [];
  const seen = new Set<string>();
  let budget = INSTRUCTION_BUNDLE_CHAR_LIMIT;
  for (const entry of ordered) {
    const exclude = (reason: InstructionExclusionReason) => excluded.push({ key: entry.key, layer: entry.layer, reason });
    // With a canonical resolution, a copied excerpt, a lesson, or a playbook key cannot
    // occupy the owner contract. Only the execution-time entry is kept.
    if (canonical && (entry.layer === 'owner_contract' || entry.key === 'owner:contract') && !canonicalOwnerContractEntry(entry)) {
      exclude('requires_owner_authority');
      continue;
    }
    // The owner contract and the current task are the live assignment, validated by
    // their producer (e.g. the run brief); they are never dropped for length. Every
    // durable layer below them is bounded and secret-screened.
    const current = CURRENT_LAYERS.has(entry.layer);
    const shaped = current
      ? entry.lines.length > 0 && entry.lines.every((line) => typeof line === 'string')
      : boundedLines(entry.lines);
    if (!INSTRUCTION_LAYERS.includes(entry.layer) || entry.key.trim() === '' || seen.has(entry.key) || !shaped) {
      exclude('invalid');
      continue;
    }
    const text = entry.lines.join('\n');
    if (current) {
      seen.add(entry.key);
      entries.push(entry);
      continue;
    }
    if (secretShaped(text)) { exclude('secret_shaped'); continue; }
    if (entry.layer === 'learned_heuristic' && OWNER_AUTHORITY_DOMAIN.test(text)) {
      exclude('requires_owner_authority');
      continue;
    }
    if (text.length > budget) { exclude('over_budget'); continue; }
    budget -= text.length;
    seen.add(entry.key);
    entries.push(entry);
  }

  if (canonical && !entries.some((entry) => entry.key === 'owner:contract' && entry.layer === 'owner_contract')) {
    entries.unshift(UNKNOWN_OWNER_CONTRACT);
  }

  const version = fingerprint(JSON.stringify({
    role: input.role,
    playbook: playbook === null ? null : [playbook.role, playbook.version, playbook.provenance.version],
    entries: entries.map((entry) => [entry.layer, entry.key, entry.provenance.version, entry.lines]),
  }));
  return { role: input.role, playbook, entries, excluded, version };
}

function playbookSection(role: string, playbook: RolePlaybook | null): string[] {
  if (playbook === null) {
    return [
      `Role: ${role} — Unknown. No active role playbook was resolved for this role.`,
      'Do not act as an undefined generic AI: follow the owner contract and the current task only, and report that your role playbook is missing.',
    ];
  }
  const list = (title: string, items: readonly string[]) => items.length === 0 ? [] : [`${title}:`, ...items.map((item) => `- ${item}`)];
  return [
    `You are ${playbook.identity}. Your standing job is ${playbook.standingJob}`,
    ...list('You watch', playbook.watches),
    ...list('You produce', playbook.produces),
    ...list('You must not', playbook.mustNot),
    `You report to: ${playbook.reportsTo}`,
    ...list('After a fresh session or restart, re-orient from', playbook.reorientFrom),
  ];
}

/**
 * The bundle as prompt text: the role header first, then every layer in
 * authority order. Only what was resolved is rendered; exclusions are recorded
 * on the bundle, never replayed.
 */
export function renderInstructionBundle(bundle: InstructionBundle): string {
  const out: string[] = [
    ...playbookSection(bundle.role, bundle.playbook),
    '',
    `Instruction bundle ${bundle.version}. Sections below are in authority order: a later section never overrides an earlier one, and nothing here grants a permission.`,
  ];
  for (const layer of INSTRUCTION_LAYERS) {
    const entries = bundle.entries.filter((entry) => entry.layer === layer);
    if (entries.length === 0) continue;
    out.push('', `${LAYER_TITLE[layer]}:`);
    for (const entry of entries) out.push(...entry.lines);
  }
  return out.join('\n');
}

/** Audit view of a consumed bundle: identities and versions, never instruction text. */
export function instructionBundleAudit(bundle: InstructionBundle): {
  readonly role: string;
  readonly version: string;
  readonly playbook: string | null;
  readonly sources: readonly string[];
  readonly excluded: readonly string[];
} {
  return {
    role: bundle.role,
    version: bundle.version,
    playbook: bundle.playbook === null ? null : `${bundle.playbook.role}@${bundle.playbook.version}`,
    sources: bundle.entries.map((entry) => `${entry.layer}:${entry.provenance.source}:${entry.provenance.ref}${entry.provenance.version === null ? '' : `@${entry.provenance.version}`}`),
    excluded: bundle.excluded.map((item) => `${item.layer}:${item.key}:${item.reason}`),
  };
}

function isStringList(value: unknown, limit: number): value is string[] {
  return Array.isArray(value) && value.length <= limit
    && value.every((item) => typeof item === 'string' && item.trim() !== '' && item.length <= INSTRUCTION_LINE_LIMIT);
}

function isBoundedText(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '' && value.length <= INSTRUCTION_LINE_LIMIT;
}

/**
 * Parse a playbook seed catalog ({ playbooks: [...] }). Invalid or duplicate
 * roles are dropped, so a bad seed shows as a missing (Unknown) role rather
 * than a partially trusted one.
 */
export function parseRolePlaybooks(value: unknown, source: string): RolePlaybook[] {
  if (value === null || typeof value !== 'object' || !Array.isArray((value as { playbooks?: unknown }).playbooks)) return [];
  const out: RolePlaybook[] = [];
  const seen = new Set<string>();
  for (const raw of (value as { playbooks: unknown[] }).playbooks) {
    if (raw === null || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    const role = item['role'];
    if (typeof role !== 'string' || !/^[a-z][a-z0-9_]{0,39}$/.test(role) || seen.has(role)) continue;
    if (!isBoundedText(item['identity']) || !isBoundedText(item['standingJob']) || !isBoundedText(item['reportsTo'])) continue;
    if (typeof item['version'] !== 'string' || !/^[A-Za-z0-9._-]{1,40}$/.test(item['version'])) continue;
    const watches = item['watches'] ?? [];
    const produces = item['produces'] ?? [];
    const mustNot = item['mustNot'] ?? [];
    const reorientFrom = item['reorientFrom'] ?? [];
    if (![watches, produces, mustNot, reorientFrom].every((list) => isStringList(list, INSTRUCTION_ENTRY_LINE_LIMIT))) continue;
    seen.add(role);
    out.push({
      role,
      identity: item['identity'],
      standingJob: item['standingJob'],
      watches: watches as string[],
      produces: produces as string[],
      mustNot: mustNot as string[],
      reportsTo: item['reportsTo'],
      reorientFrom: reorientFrom as string[],
      version: item['version'],
      provenance: { source, ref: role, version: item['version'] },
    });
  }
  return out;
}
